/**
 * EWSDistributionChart - Bar chart component for risk indicator distributions
 *
 * Displays a vertical bar chart showing customer distribution across risk levels
 * (LOW, MEDIUM, HIGH) for a single risk indicator. Includes hover tooltips showing
 * exact counts.
 */

import React, { useState, useMemo } from 'react';
import { IndicatorDistribution, DistributionDataPoint, RiskLevel, formatIndianNumber } from '../../../types/ews.types';

export interface EWSDistributionChartProps {
  /** Distribution data for the indicator */
  distribution: IndicatorDistribution;
  /** Callback when a bar is clicked */
  onBarClick?: (indicator: string, indicatorName: string, level: RiskLevel, levelLabel: string, count: number) => void;
}

/**
 * Get bar color based on risk level.
 * LOW = green, MEDIUM = pale yellow/amber, HIGH = maroon/red
 * Colors are at 80% opacity (20% transparent)
 */
function getBarColor(level: string): string {
  switch (level) {
    case 'LOW':
      return 'rgba(34, 197, 94, 0.8)'; // green-500 at 80%
    case 'MEDIUM':
      return 'rgba(245, 158, 11, 0.8)'; // amber-500 at 80%
    case 'HIGH':
      return 'rgba(220, 38, 38, 0.8)'; // red-600 at 80%
    default:
      return 'rgba(156, 163, 175, 0.8)'; // gray-400 at 80%
  }
}

/**
 * Individual bar component with hover state and click handler.
 */
const Bar: React.FC<{
  dataPoint: DistributionDataPoint;
  heightPercent: number;
  maxHeight: number;
  onClick?: () => void;
}> = ({ dataPoint, heightPercent, maxHeight, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const barColor = getBarColor(dataPoint.level);

  // Calculate bar height - minimum 4px for visibility when count is 0
  const barHeight = heightPercent > 0 ? Math.max((heightPercent / 100) * maxHeight, 4) : 4;

  return (
    <div
      className="flex flex-col items-center flex-1 relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Tooltip */}
      {isHovered && (
        <div
          className="absolute bottom-full mb-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-md shadow-lg z-10 whitespace-nowrap"
          style={{ minWidth: '80px', textAlign: 'center' }}
        >
          <div className="font-medium">{dataPoint.label}</div>
          <div>Customers : {formatIndianNumber(dataPoint.count)}</div>
          {/* Tooltip arrow */}
          <div
            className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid #1F2937',
            }}
          />
        </div>
      )}

      {/* Bar container */}
      <div
        className="w-full flex items-end justify-center"
        style={{ height: `${maxHeight}px` }}
      >
        <div
          className="rounded-t transition-all duration-200 cursor-pointer"
          onClick={onClick}
          style={{
            width: '60px',
            height: `${barHeight}px`,
            backgroundColor: barColor,
            opacity: isHovered ? 0.85 : 1,
            transform: isHovered ? 'scaleY(1.02)' : 'scaleY(1)',
            transformOrigin: 'bottom',
          }}
        />
      </div>

      {/* Label */}
      <span
        className="text-xs text-gray-700 mt-2 text-center leading-tight"
        style={{
          fontSize: '10px',
          maxWidth: '70px',
          wordWrap: 'break-word',
        }}
      >
        {dataPoint.label}
      </span>
    </div>
  );
};

export const EWSDistributionChart: React.FC<EWSDistributionChartProps> = ({
  distribution,
  onBarClick,
}) => {
  // Calculate max value and height percentages
  const { maxValue, heightPercents } = useMemo(() => {
    const counts = distribution.data.map(d => d.count);
    const max = Math.max(...counts, 1); // Ensure at least 1 to avoid division by 0
    const percents = counts.map(c => (c / max) * 100);
    return { maxValue: max, heightPercents: percents };
  }, [distribution.data]);

  // Generate Y-axis labels based on max value
  const yAxisLabels = useMemo(() => {
    const step = maxValue / 4;
    return [
      Math.round(maxValue),
      Math.round(step * 3),
      Math.round(step * 2),
      Math.round(step),
      0,
    ];
  }, [maxValue]);

  // Format Y-axis label (e.g., 40000 -> 40K)
  const formatYAxisLabel = (value: number): string => {
    if (value >= 1000) {
      return `${Math.round(value / 1000)}K`;
    }
    return value.toString();
  };

  const maxBarHeight = 250; // pixels - overall chart area height

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
      {/* Header */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">{distribution.name}</h4>
        <p className="text-xs text-gray-600 mt-0.5">{distribution.description}</p>
      </div>

      {/* Chart area */}
      <div className="flex">
        {/* Y-axis labels */}
        <div
          className="flex flex-col justify-between text-right pr-2"
          style={{ height: `${maxBarHeight}px` }}
        >
          {yAxisLabels.map((label, index) => (
            <span
              key={index}
              className="text-xs text-gray-400"
              style={{ fontSize: '10px', lineHeight: '1' }}
            >
              {formatYAxisLabel(label)}
            </span>
          ))}
        </div>

        {/* Bars container */}
        <div className="flex-1 relative">
          {/* Grid lines */}
          <div
            className="absolute inset-0 flex flex-col justify-between pointer-events-none"
            style={{ height: `${maxBarHeight}px` }}
          >
            {[0, 1, 2, 3, 4].map(i => (
              <div
                key={i}
                className="w-full border-t border-dashed border-gray-100"
              />
            ))}
          </div>

          {/* Bars */}
          <div className="flex justify-around items-end relative z-10">
            {distribution.data.map((dataPoint, index) => (
              <Bar
                key={dataPoint.level}
                dataPoint={dataPoint}
                heightPercent={heightPercents[index]}
                maxHeight={maxBarHeight}
                onClick={() => onBarClick?.(
                  distribution.indicator,
                  distribution.name,
                  dataPoint.level as RiskLevel,
                  dataPoint.label,
                  dataPoint.count
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EWSDistributionChart;
